import { createSlice } from '@reduxjs/toolkit'
import { adminOrganizationApi } from '../api/admin-organization-api'
import { adminOrganizationAccountApi } from '../api/admin-organization-account-api'

import slicesNotifications from './notifications'

const initialState = {
    status: {
        initialized: false,
        processing: false,
        has_error: false,
        error_message: null
    },
    listView: {
        items: []
    },
    infoView: {
        item: null,
        accounts: []
    }
}

const slice = createSlice({
    name: 'admin-organizations',
    initialState,
    reducers: {
        initializeRequest(state, action) {
            state.status = {
                initialized: false,
                processing: true,
                has_error: false,
                error_message: null
            }
        },

        initializeFailed(state, action) {
            state.status = {
                initialized: false,
                processing: false,
                has_error: true,
                error_message: action.payload.errorMessage
            }
        },

        updateListView(state, action) {
            const { body } = action.payload
            const { listView, infoView } = body || {}

            state.status = {
                initialized: true,
                processing: false,
                has_error: false,
                error_message: null
            }

            state.listView = {
                items: listView.items || []
            }

            if (infoView) {
                state.infoView = {
                    item: infoView.item || null,
                    accounts: infoView.accounts || []
                }
            }
        },

        updateInfoView(state, action) {
            const { body } = action.payload
            const { listView, infoView } = body || {}

            state.status = {
                initialized: true,
                processing: false,
                has_error: false,
                error_message: null
            }

            state.listView = {
                items: listView.items || []
            }

            state.infoView = {
                item: infoView.item || null,
                accounts: infoView.accounts || []
            }
        }
    }
})

export const { reducer } = slice

export const loadList = () => async (dispatch) => {
    dispatch(slice.actions.initializeRequest(data))

    const data = await adminOrganizationApi.list()
    if (data.statusCode !== 200) {
        dispatch(slice.actions.initializeFailed(data))
        dispatch(
            slicesNotifications.addApiError(data, 'Loading Organization Data')
        )
    } else {
        dispatch(slice.actions.updateListView(data))
    }
}

export const loadOrganizationRequest =
    (organization_id) => async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationApi.info(organization_id)

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(
                    data,
                    'Loading Organization Data'
                )
            )
        } else {
            dispatch(slice.actions.updateInfoView(data))
        }
    }

export const createRequest =
    (
        owner_user_id,
        org_name,
        org_short_code,
        org_is_enabled,
        org_contact_first_name,
        org_contact_last_name,
        org_contact_email,
        org_contact_phone_number,
        org_company_name,
        org_enabled_countries,
        organization_billing_profile_id
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationApi.create(
            owner_user_id,
            org_name,
            org_short_code,
            org_is_enabled,
            org_contact_first_name,
            org_contact_last_name,
            org_contact_email,
            org_contact_phone_number,
            org_company_name,
            org_enabled_countries,
            organization_billing_profile_id
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(
                slicesNotifications.addApiError(data, 'Creating Organization')
            )
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization',
                    'Organization created'
                )
            )
        }

        return data
    }

export const updateRequest =
    (
        organization_id,
        org_name,
        org_short_code,
        org_is_enabled,
        org_contact_first_name,
        org_contact_last_name,
        org_contact_email,
        org_contact_phone_number,
        org_company_name,
        org_enabled_countries,
        organization_billing_profile_id,
        org_audience_target_countries_enabled,
        org_geofence_countries_enabled
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationApi.update(
            organization_id,
            org_name,
            org_short_code,
            org_is_enabled,
            org_contact_first_name,
            org_contact_last_name,
            org_contact_email,
            org_contact_phone_number,
            org_company_name,
            org_enabled_countries,
            organization_billing_profile_id,
            org_audience_target_countries_enabled,
            org_geofence_countries_enabled
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(slicesNotifications.addApiError(data, 'Organization Info'))
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Info',
                    'Changes Saved'
                )
            )
        }

        return data
    }

export const updateAccountRequest =
    (
        organization_id,
        organization_account_id,
        parent_organization_account_id,
        organization_billing_profile_id,
        account_name,
        account_type,
        is_enabled
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationAccountApi.update(
            organization_id,
            organization_account_id,
            parent_organization_account_id,
            organization_billing_profile_id,
            account_name,
            account_type,
            is_enabled
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(slicesNotifications.addApiError(data, 'Organization Info'))
        } else {
            dispatch(slice.actions.updateInfoView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Info',
                    'Changes Saved'
                )
            )
        }

        return data
    }
export const createAccountRequest =
    (
        organization_id,
        organization_account_id,
        parent_organization_account_id,
        organization_billing_profile_id,
        account_name,
        account_type,
        is_enabled
    ) =>
    async (dispatch) => {
        dispatch(slice.actions.initializeRequest(data))

        const data = await adminOrganizationAccountApi.create(
            organization_id,
            organization_account_id,
            parent_organization_account_id,
            organization_billing_profile_id,
            account_name,
            account_type,
            is_enabled
        )

        if (data.statusCode !== 200) {
            dispatch(slice.actions.initializeFailed(data))
            dispatch(slicesNotifications.addApiError(data, 'Organization Info'))
        } else {
            dispatch(slice.actions.updateListView(data))
            dispatch(
                slicesNotifications.addApiSuccess(
                    data,
                    'Organization Info',
                    'Changes Saved'
                )
            )
        }

        return data
    }
